import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useVisitedPageCache, visitedPageKey } from "../../utils/visitedPageCache";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  erpFetch,
  isErpApiSuccess,
  getErpRequestErrorMessage,
  getActiveBranchId,
  listBankAccounts,
  generateIdempotencyKey,
  listCustomerUsers,
} from "../../erpClient";
import Pagination from "../../components/Pagination";
import { useButtonTableStyle } from "../../utils/useButtonTableStyle";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { formatIndianRupee } from "../../utils/formatLiveRate";

const PAYMENT_MODES = ["CASH", "UPI", "CARD", "BANK", "CHEQUE"];
const STATUS_OPTIONS = ["", "ACTIVE", "MATURED", "REDEEMED", "CLOSED", "CANCELLED"];
const TODAY = new Date().toISOString().split("T")[0];

// ─── Status Badge Component ──────────────────────────────────────────────────

function StatusBadge({ status }) {
  switch (status) {
    case "ACTIVE":
      return <span className="badge bg-success-subtle text-success">ACTIVE</span>;
    case "MATURED":
      return <span className="badge bg-primary-subtle text-primary">MATURED</span>;
    case "REDEEMED":
      return <span className="badge bg-info-subtle text-info">REDEEMED</span>;
    case "CLOSED":
      return <span className="badge bg-secondary-subtle text-secondary">CLOSED</span>;
    case "CANCELLED":
      return <span className="badge bg-danger-subtle text-danger">CANCELLED</span>;
    default:
      return <span className="badge bg-light text-dark">{status || "—"}</span>;
  }
}

function EntryTypeBadge({ type }) {
  switch (type) {
    case "PAYMENT":
      return <span className="badge bg-success-subtle text-success">PAYMENT</span>;
    case "SKIP":
      return <span className="badge bg-warning-subtle text-warning">SKIP</span>;
    case "LATE_FEE":
      return <span className="badge bg-danger-subtle text-danger">LATE_FEE</span>;
    default:
      return <span className="badge bg-light text-dark">{type || "—"}</span>;
  }
}

function numAmt(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// ─── Customer Picker Component ───────────────────────────────────────────────

function CustomerPicker({ value, onChange, buttonClass }) {
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!search.trim()) {
      setOptions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await listCustomerUsers({ search, limit: 20 });
        const items = res?.data?.items ?? res?.data ?? [];
        setOptions(Array.isArray(items) ? items : []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  return (
    <div>
      <div className="position-relative">
        <Form.Control
          placeholder="Search customer by name or mobile..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`mb-2 ${buttonClass}`}
        />
        {loading && (
          <div
            className="position-absolute text-muted small"
            style={{ right: 12, top: 10 }}
          >
            <Icon icon="solar:refresh-line-duotone" className="spin" />
          </div>
        )}
      </div>

      {options.length > 0 && (
        <div
          className="border rounded-2 shadow-sm overflow-auto mb-2 bg-white"
          style={{ maxHeight: 200, zIndex: 1050 }}
        >
          {options.map((c) => (
            <div
              key={c.id}
              className="px-3 py-2 cursor-pointer border-bottom d-flex justify-content-between align-items-center"
              style={{
                cursor: "pointer",
                background: value?.id === c.id ? "#EFF6FF" : "transparent",
              }}
              onClick={() => {
                onChange(c);
                setSearch(`${c.name || ""} ${c.mobile || ""}`.trim());
                setOptions([]);
              }}
            >
              <div>
                <span className="fw-semibold text-dark">{c.name}</span>
                {c.city && <span className="ms-2 text-muted small">({c.city})</span>}
              </div>
              {c.mobile && (
                <span className="badge bg-light text-muted small">{c.mobile}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {value && (
        <div className="p-2 rounded bg-success-subtle text-success small d-flex align-items-center">
          <Icon icon="solar:check-circle-line-duotone" className="me-2 fs-5" />
          <div>
            <strong>Selected:</strong> {value.name} {value.mobile && `(${value.mobile})`}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Entry Edit Modal ─────────────────────────────────────────────────────────

function EntryEditModal({
  show,
  onHide,
  entry,
  bankAccounts,
  valueModeIsGoldWeight,
  onSave,
}) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [form, setForm] = useState({
    receipt_date: "",
    amount: "",
    payment_mode: "CASH",
    bank_account_id: "",
    gold_rate: "",
    remarks: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry) {
      setForm({
        receipt_date: entry.receipt_date?.split("T")[0] || TODAY,
        amount: entry.amount ?? "",
        payment_mode: entry.payment_mode || "CASH",
        bank_account_id: entry.bank_account_id ?? "",
        gold_rate: entry.gold_rate ?? "",
        remarks: entry.remarks ?? "",
      });
    }
  }, [entry]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type: "update_entry",
        entry_id: entry.id,
        receipt_date: form.receipt_date,
        remarks: form.remarks,
        payment_mode: form.payment_mode,
      };
      if (form.amount !== "") payload.amount = Number(form.amount);
      if (form.bank_account_id) payload.bank_account_id = Number(form.bank_account_id);
      if (valueModeIsGoldWeight && form.gold_rate !== "") payload.gold_rate = Number(form.gold_rate);

      await onSave(payload);
      onHide();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      contentClassName={cardClass}
    >
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <Modal.Title className="fs-5 fw-semibold">
          Edit Payment Entry — Month #{entry?.installment_no}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body className="p-4">
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Receipt Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.receipt_date}
                  onChange={(e) => setF("receipt_date", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.amount}
                  onChange={(e) => setF("amount", e.target.value)}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold small">Payment Mode</Form.Label>
                    <Form.Select
                      value={form.payment_mode}
                      onChange={(e) => setF("payment_mode", e.target.value)}
                      className={buttonClass}
                    >
                      {PAYMENT_MODES.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                {["BANK", "CHEQUE", "UPI", "CARD"].includes(form.payment_mode) && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Bank Account</Form.Label>
                      <Form.Select
                        value={form.bank_account_id}
                        onChange={(e) => setF("bank_account_id", e.target.value)}
                        className={buttonClass}
                      >
                        <option value="">— Select Bank Account —</option>
                        {bankAccounts.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.bank_name} - {b.account_number}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
                {valueModeIsGoldWeight && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Gold Rate (₹/gm)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min={0}
                        value={form.gold_rate}
                        onChange={(e) => setF("gold_rate", e.target.value)}
                        className={buttonClass}
                      />
                    </Form.Group>
                  </Col>
                )}
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.remarks}
                  onChange={(e) => setF("remarks", e.target.value)}
                  className={buttonClass}
                  placeholder="Optional transaction remarks..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className={buttonClass} onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" className={buttonClass} type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save Entry"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── Receive Payment Modal ───────────────────────────────────────────────────

function ReceiveModal({
  show,
  onHide,
  enrollment,
  selectedPlan,
  bankAccounts,
  onSaved,
}) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const valueModeIsGoldWeight = selectedPlan?.value_mode === "GOLD_WEIGHT";

  const [form, setForm] = useState({
    installment_no: "",
    receipt_date: TODAY,
    amount: "",
    payment_mode: "CASH",
    bank_account_id: "",
    gold_rate: "",
    remarks: "",
  });
  const [saving, setSaving] = useState(false);
  const idempotencyKeyRef = useRef("");

  useEffect(() => {
    if (show && enrollment) {
      idempotencyKeyRef.current = generateIdempotencyKey();
      const nextInst = (enrollment.paid_count ?? 0) + 1;
      setForm({
        installment_no: nextInst,
        receipt_date: TODAY,
        amount: enrollment.installment_amount ?? selectedPlan?.default_amount ?? "",
        payment_mode: "CASH",
        bank_account_id: "",
        gold_rate: "",
        remarks: "",
      });
    }
  }, [show, enrollment, selectedPlan]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.installment_no) {
      toast.error("Installment number is required.");
      return;
    }
    if (!form.receipt_date) {
      toast.error("Receipt date is required.");
      return;
    }
    if (valueModeIsGoldWeight && !form.gold_rate) {
      toast.error("Gold rate is required for gold weight plans.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: "receipt",
        enrollment_id: enrollment.enrollment_id ?? enrollment.id,
        installment_no: Number(form.installment_no),
        receipt_date: form.receipt_date,
        amount: form.amount !== "" ? Number(form.amount) : undefined,
        payment_mode: form.payment_mode,
        remarks: form.remarks,
        idempotency_key: idempotencyKeyRef.current,
      };
      if (form.bank_account_id) payload.bank_account_id = Number(form.bank_account_id);
      if (valueModeIsGoldWeight) payload.gold_rate = Number(form.gold_rate);

      const res = await erpFetch("/scheme", {
        method: "POST",
        body: payload,
      });
      if (isErpApiSuccess(res)) {
        toast.success("Payment received successfully.");
        onSaved(res.data);
        onHide();
      } else {
        toast.error(res?.message || "Failed to save payment.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Failed to save payment."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      contentClassName={cardClass}
    >
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <div>
          <Modal.Title className="fs-5 fw-semibold mb-0">Receive Payment</Modal.Title>
          <div className="text-muted small mt-1">
            Card #{enrollment?.card_no} — {enrollment?.customer_name}
          </div>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body className="p-4">
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Installment #</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={form.installment_no}
                  onChange={(e) => setF("installment_no", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Receipt Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.receipt_date}
                  onChange={(e) => setF("receipt_date", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.amount}
                  onChange={(e) => setF("amount", e.target.value)}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Payment Mode</Form.Label>
                <Form.Select
                  value={form.payment_mode}
                  onChange={(e) => setF("payment_mode", e.target.value)}
                  className={buttonClass}
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            {["BANK", "CHEQUE", "UPI", "CARD"].includes(form.payment_mode) && (
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Bank Account</Form.Label>
                  <Form.Select
                    value={form.bank_account_id}
                    onChange={(e) => setF("bank_account_id", e.target.value)}
                    className={buttonClass}
                  >
                    <option value="">— Select Bank Account —</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.bank_name} - {b.account_number}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            {valueModeIsGoldWeight && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Gold Rate (₹/gm) *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.gold_rate}
                    onChange={(e) => setF("gold_rate", e.target.value)}
                    className={buttonClass}
                    required
                  />
                </Form.Group>
              </Col>
            )}
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.remarks}
                  onChange={(e) => setF("remarks", e.target.value)}
                  className={buttonClass}
                  placeholder="Optional payment notes..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className={buttonClass} onClick={onHide}>
            Cancel
          </Button>
          <Button variant="success" className={buttonClass} type="submit" disabled={saving}>
            {saving ? "Processing..." : "Receive Payment"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}



// ─── Close Card Modal ────────────────────────────────────────────────────────

function CloseModal({ show, onHide, enrollment, bankAccounts, onSaved }) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const isMandal = Number(enrollment?.is_mandal) === 1;
  const [form, setForm] = useState({
    close_type: "WITHDRAW",
    refund_amount: "",
    payment_mode: "CASH",
    bank_account_id: "",
    close_date: TODAY,
    remarks: "",
  });
  const [saving, setSaving] = useState(false);
  const hasPayments = (enrollment?.paid_count ?? 0) > 0;

  useEffect(() => {
    if (show) {
      setForm({
        close_type: hasPayments ? (isMandal ? "WITHDRAW" : "CLOSED") : "CANCELLED",
        refund_amount: "",
        payment_mode: "CASH",
        bank_account_id: "",
        close_date: TODAY,
        remarks: "",
      });
    }
  }, [show, hasPayments, isMandal]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type: "close",
        enrollment_id: enrollment.enrollment_id ?? enrollment.id,
        close_type: form.close_type,
        close_date: form.close_date,
        remarks: form.remarks,
        payment_mode: form.payment_mode,
      };
      if (form.refund_amount !== "") {
        const amt = Number(form.refund_amount);
        if (form.close_type === "WITHDRAW") payload.withdraw_amount = amt;
        else payload.refund_amount = amt;
      }
      if (form.bank_account_id) payload.bank_account_id = Number(form.bank_account_id);

      const res = await erpFetch("/scheme", { method: "POST", body: payload });
      if (isErpApiSuccess(res)) {
        toast.success("Card closed successfully.");
        onSaved(res.data);
        onHide();
      } else {
        toast.error(res?.message || "Failed to close card.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Failed to close card."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      contentClassName={cardClass}
    >
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <div>
          <Modal.Title className="fs-5 fw-semibold mb-0">Close Scheme Card</Modal.Title>
          <div className="text-muted small mt-1">
            Card #{enrollment?.card_no} — {enrollment?.customer_name}
          </div>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body className="p-4">
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Close Action</Form.Label>
                <Form.Select
                  value={form.close_type}
                  onChange={(e) => setF("close_type", e.target.value)}
                  className={buttonClass}
                >
                  <option value="WITHDRAW">WITHDRAW (Payout)</option>
                  <option value="CLOSED">CLOSED (Settlement)</option>
                  {!hasPayments && <option value="CANCELLED">CANCELLED (Zero Payments)</option>}
                </Form.Select>
                {isMandal && (
                  <div className="text-muted small mt-1">
                    Prefer Withdraw action for Mandal cards when possible.
                  </div>
                )}
                {!hasPayments && (
                  <div className="text-muted small mt-1">
                    CANCELLED is available because no payments were recorded yet.
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Close Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.close_date}
                  onChange={(e) => setF("close_date", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            {(form.close_type === "CLOSED" || form.close_type === "WITHDRAW") && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">
                    {form.close_type === "WITHDRAW" ? "Withdraw Amount (₹)" : "Refund Amount (₹)"}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.refund_amount}
                    onChange={(e) => setF("refund_amount", e.target.value)}
                    className={buttonClass}
                    placeholder="Enter amount if applicable"
                  />
                  <div className="text-muted small mt-1">
                    Withdrawable:{" "}
                    {formatIndianRupee(
                      enrollment?.withdrawable_amount ?? enrollment?.available_amount ?? 0,
                    )}
                  </div>
                </Form.Group>
              </Col>
            )}
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Payment Mode</Form.Label>
                <Form.Select
                  value={form.payment_mode}
                  onChange={(e) => setF("payment_mode", e.target.value)}
                  className={buttonClass}
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            {["BANK", "CHEQUE", "UPI", "CARD"].includes(form.payment_mode) && (
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Bank Account</Form.Label>
                  <Form.Select
                    value={form.bank_account_id}
                    onChange={(e) => setF("bank_account_id", e.target.value)}
                    className={buttonClass}
                  >
                    <option value="">— Select Bank Account —</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.bank_name} - {b.account_number}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Closing Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.remarks}
                  onChange={(e) => setF("remarks", e.target.value)}
                  className={buttonClass}
                  placeholder="Reason for closure..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className={buttonClass} onClick={onHide}>
            Cancel
          </Button>
          <Button variant="danger" className={buttonClass} type="submit" disabled={saving}>
            {saving ? "Closing..." : "Close Card"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── Withdraw Modal ──────────────────────────────────────────────────────────

function WithdrawModal({ show, onHide, enrollment, bankAccounts, onSaved }) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const paid = numAmt(enrollment?.paid_amount);
  const bonus = numAmt(enrollment?.bonus_amount);
  const lateFee = numAmt(enrollment?.late_fee_total);
  const redeemed = numAmt(enrollment?.redeemed_amount);
  const openLoan =
    numAmt(enrollment?.open_loan_principal) + numAmt(enrollment?.open_loan_interest);
  const withdrawable =
    enrollment?.withdrawable_amount != null
      ? numAmt(enrollment.withdrawable_amount)
      : paid + bonus - lateFee - redeemed - openLoan;

  const [form, setForm] = useState({
    withdraw_amount: "",
    payment_mode: "CASH",
    bank_account_id: "",
    withdraw_date: TODAY,
    remarks: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setForm({
        withdraw_amount: withdrawable > 0 ? String(withdrawable) : "",
        payment_mode: "CASH",
        bank_account_id: "",
        withdraw_date: TODAY,
        remarks: "",
      });
    }
  }, [show, withdrawable]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const hasOpenLoan = openLoan > 0;

  const handleSave = async (e) => {
    e.preventDefault();
    if (hasOpenLoan) {
      toast.error("Clear open loan principal/interest before withdraw.");
      return;
    }
    if (!(Number(form.withdraw_amount) > 0)) {
      toast.error("Withdraw amount is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: "withdraw",
        enrollment_id: enrollment.enrollment_id ?? enrollment.id,
        withdraw_amount: Number(form.withdraw_amount),
        payment_mode: form.payment_mode,
        withdraw_date: form.withdraw_date,
        remarks: form.remarks,
      };
      if (form.bank_account_id) payload.bank_account_id = Number(form.bank_account_id);

      const res = await erpFetch("/scheme", { method: "POST", body: payload });
      if (isErpApiSuccess(res)) {
        toast.success("Withdraw completed successfully.");
        onSaved(res.data);
        onHide();
      } else {
        toast.error(res?.message || "Withdraw failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Withdraw failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" contentClassName={cardClass}>
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <div>
          <Modal.Title className="fs-5 fw-semibold mb-0">Withdraw</Modal.Title>
          <div className="text-muted small mt-1">
            Card #{enrollment?.card_no} — {enrollment?.customer_name}
          </div>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body className="p-4">
          <div className="p-3 rounded-3 border mb-3" style={{ background: "#F8FAFC" }}>
            <div className="small text-muted mb-2 fw-semibold">Payout formula</div>
            <div className="d-flex flex-column gap-1 small">
              <div className="d-flex justify-content-between">
                <span>Hafta (paid)</span>
                <span className="fw-semibold">{formatIndianRupee(paid)}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>+ Bonus</span>
                <span className="fw-semibold">{formatIndianRupee(bonus)}</span>
              </div>
              <div className="d-flex justify-content-between text-danger">
                <span>− Late fee</span>
                <span className="fw-semibold">{formatIndianRupee(lateFee)}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>− Redeemed</span>
                <span className="fw-semibold">{formatIndianRupee(redeemed)}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>− Open loan</span>
                <span className="fw-semibold">{formatIndianRupee(openLoan)}</span>
              </div>
              <hr className="my-2" />
              <div className="d-flex justify-content-between fw-bold">
                <span>Withdrawable</span>
                <span className="text-primary">{formatIndianRupee(withdrawable)}</span>
              </div>
            </div>
          </div>
          {hasOpenLoan && (
            <div className="alert alert-warning py-2 small">
              Open loan must be repaid before withdraw.
            </div>
          )}
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Withdraw Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.withdraw_amount}
                  onChange={(e) => setF("withdraw_amount", e.target.value)}
                  className={buttonClass}
                  required
                  disabled={hasOpenLoan}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Withdraw Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.withdraw_date}
                  onChange={(e) => setF("withdraw_date", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Payment Mode</Form.Label>
                <Form.Select
                  value={form.payment_mode}
                  onChange={(e) => setF("payment_mode", e.target.value)}
                  className={buttonClass}
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            {["BANK", "CHEQUE", "UPI", "CARD"].includes(form.payment_mode) && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Bank Account</Form.Label>
                  <Form.Select
                    value={form.bank_account_id}
                    onChange={(e) => setF("bank_account_id", e.target.value)}
                    className={buttonClass}
                  >
                    <option value="">— Select Bank Account —</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.bank_name} - {b.account_number}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.remarks}
                  onChange={(e) => setF("remarks", e.target.value)}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className={buttonClass} onClick={onHide}>Cancel</Button>
          <Button
            variant="primary"
            className={buttonClass}
            type="submit"
            disabled={saving || hasOpenLoan}
          >
            {saving ? "Processing..." : "Confirm Withdraw"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── Loan Create Modal ───────────────────────────────────────────────────────

function LoanCreateModal({ show, onHide, enrollment, bankAccounts, onSaved }) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [pot, setPot] = useState(null);
  const [loadingPot, setLoadingPot] = useState(false);
  const [form, setForm] = useState({
    principal: "",
    interest_amount: "",
    interest_rate: "",
    emi_amount: "",
    loan_date: TODAY,
    payment_mode: "CASH",
    bank_account_id: "",
    remarks: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || !enrollment?.plan_id) return;
    setForm({
      principal: "",
      interest_amount: "",
      interest_rate: "",
      emi_amount: "",
      loan_date: TODAY,
      payment_mode: "CASH",
      bank_account_id: "",
      remarks: "",
    });
    setLoadingPot(true);
    erpFetch("/scheme", {
      method: "POST",
      body: { type: "loan_pot", plan_id: enrollment.plan_id },
    })
      .then((res) => {
        if (isErpApiSuccess(res)) setPot(res.data);
        else {
          setPot(null);
          toast.error(res?.message || "Failed to load loan pot.");
        }
      })
      .catch((err) => {
        setPot(null);
        toast.error(getErpRequestErrorMessage(err, "Failed to load loan pot."));
      })
      .finally(() => setLoadingPot(false));
  }, [show, enrollment?.plan_id]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const available = numAmt(pot?.available_for_loan);

  const handleSave = async (e) => {
    e.preventDefault();
    const principal = Number(form.principal);
    if (!(principal > 0)) {
      toast.error("Principal is required.");
      return;
    }
    if (principal > available) {
      toast.error(`Loan cannot exceed plan pot available (${formatIndianRupee(available)}).`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: "loan_create",
        enrollment_id: enrollment.enrollment_id ?? enrollment.id,
        principal,
        interest_amount: form.interest_amount !== "" ? Number(form.interest_amount) : 0,
        interest_rate: form.interest_rate !== "" ? Number(form.interest_rate) : 0,
        emi_amount: form.emi_amount !== "" ? Number(form.emi_amount) : 0,
        loan_date: form.loan_date,
        payment_mode: form.payment_mode,
        remarks: form.remarks,
      };
      if (form.bank_account_id) payload.bank_account_id = Number(form.bank_account_id);

      const res = await erpFetch("/scheme", { method: "POST", body: payload });
      if (isErpApiSuccess(res)) {
        toast.success("Loan created successfully.");
        onSaved(res.data?.card ?? res.data);
        onHide();
      } else {
        toast.error(res?.message || "Loan create failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Loan create failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" contentClassName={cardClass} size="lg">
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <div>
          <Modal.Title className="fs-5 fw-semibold mb-0">Give Loan</Modal.Title>
          <div className="text-muted small mt-1">
            Card #{enrollment?.card_no} — pot from this plan only
          </div>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body className="p-4">
          <div className="p-3 rounded-3 border mb-3" style={{ background: "#F8FAFC" }}>
            {loadingPot ? (
              <span className="text-muted small">Loading plan loan pot...</span>
            ) : pot ? (
              <div className="row g-2 small">
                <div className="col-4">
                  <div className="text-muted">Total gathered</div>
                  <div className="fw-bold">{formatIndianRupee(pot.total_gathered)}</div>
                </div>
                <div className="col-4">
                  <div className="text-muted">Outstanding loans</div>
                  <div className="fw-bold">{formatIndianRupee(pot.outstanding_loans)}</div>
                </div>
                <div className="col-4">
                  <div className="text-muted">Available for loan</div>
                  <div className="fw-bold text-primary">{formatIndianRupee(pot.available_for_loan)}</div>
                </div>
              </div>
            ) : (
              <span className="text-danger small">Loan pot unavailable.</span>
            )}
          </div>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Principal (₹) *</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.principal}
                  onChange={(e) => setF("principal", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Interest Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.interest_amount}
                  onChange={(e) => setF("interest_amount", e.target.value)}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Interest Rate %</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.interest_rate}
                  onChange={(e) => setF("interest_rate", e.target.value)}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">EMI Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.emi_amount}
                  onChange={(e) => setF("emi_amount", e.target.value)}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Loan Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.loan_date}
                  onChange={(e) => setF("loan_date", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Payment Mode</Form.Label>
                <Form.Select
                  value={form.payment_mode}
                  onChange={(e) => setF("payment_mode", e.target.value)}
                  className={buttonClass}
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            {["BANK", "CHEQUE", "UPI", "CARD"].includes(form.payment_mode) && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Bank Account</Form.Label>
                  <Form.Select
                    value={form.bank_account_id}
                    onChange={(e) => setF("bank_account_id", e.target.value)}
                    className={buttonClass}
                  >
                    <option value="">— Select Bank Account —</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.bank_name} - {b.account_number}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.remarks}
                  onChange={(e) => setF("remarks", e.target.value)}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className={buttonClass} onClick={onHide}>Cancel</Button>
          <Button variant="warning" className={buttonClass} type="submit" disabled={saving || loadingPot}>
            {saving ? "Creating..." : "Create Loan"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── Loan Repay Modal ────────────────────────────────────────────────────────

function LoanRepayModal({ show, onHide, enrollment, loan, bankAccounts, onSaved }) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const idempotencyKeyRef = useRef("");
  const [form, setForm] = useState({
    payment_date: TODAY,
    due_date: TODAY,
    amount: "",
    principal_amount: "",
    interest_amount: "",
    payment_mode: "CASH",
    bank_account_id: "",
    remarks: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && loan) {
      idempotencyKeyRef.current = generateIdempotencyKey();
      setForm({
        payment_date: TODAY,
        due_date: loan.next_due_date?.split?.("T")?.[0] || loan.due_date?.split?.("T")?.[0] || TODAY,
        amount: loan.emi_amount ?? "",
        principal_amount: "",
        interest_amount: "",
        payment_mode: "CASH",
        bank_account_id: "",
        remarks: "",
      });
    }
  }, [show, loan]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!(Number(form.amount) > 0)) {
      toast.error("Repay amount is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: "loan_repay",
        loan_id: loan.id,
        payment_date: form.payment_date,
        due_date: form.due_date,
        amount: Number(form.amount),
        principal_amount: form.principal_amount !== "" ? Number(form.principal_amount) : 0,
        interest_amount: form.interest_amount !== "" ? Number(form.interest_amount) : 0,
        payment_mode: form.payment_mode,
        idempotency_key: idempotencyKeyRef.current,
        remarks: form.remarks,
      };
      if (form.bank_account_id) payload.bank_account_id = Number(form.bank_account_id);

      const res = await erpFetch("/scheme", { method: "POST", body: payload });
      if (isErpApiSuccess(res)) {
        toast.success("Loan repayment recorded.");
        onSaved(res.data?.card ?? res.data);
        onHide();
      } else {
        toast.error(res?.message || "Loan repay failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Loan repay failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" contentClassName={cardClass}>
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <div>
          <Modal.Title className="fs-5 fw-semibold mb-0">Repay Loan</Modal.Title>
          <div className="text-muted small mt-1">
            Card #{enrollment?.card_no} — Loan #{loan?.id}
          </div>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body className="p-4">
          <div className="alert alert-light border small py-2">
            If payment date is after due date, a LATE_FEE entry is created automatically (display only — no cash line).
          </div>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Payment Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.payment_date}
                  onChange={(e) => setF("payment_date", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Due Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setF("due_date", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Amount (₹) *</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setF("amount", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Principal (₹)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.principal_amount}
                  onChange={(e) => setF("principal_amount", e.target.value)}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Interest (₹)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.interest_amount}
                  onChange={(e) => setF("interest_amount", e.target.value)}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Payment Mode</Form.Label>
                <Form.Select
                  value={form.payment_mode}
                  onChange={(e) => setF("payment_mode", e.target.value)}
                  className={buttonClass}
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            {["BANK", "CHEQUE", "UPI", "CARD"].includes(form.payment_mode) && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Bank Account</Form.Label>
                  <Form.Select
                    value={form.bank_account_id}
                    onChange={(e) => setF("bank_account_id", e.target.value)}
                    className={buttonClass}
                  >
                    <option value="">— Select Bank Account —</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.bank_name} - {b.account_number}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.remarks}
                  onChange={(e) => setF("remarks", e.target.value)}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className={buttonClass} onClick={onHide}>Cancel</Button>
          <Button variant="success" className={buttonClass} type="submit" disabled={saving}>
            {saving ? "Saving..." : "Repay Loan"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── Bonus Add Modal ─────────────────────────────────────────────────────────

function BonusAddModal({ show, onHide, enrollment, onSaved }) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [form, setForm] = useState({ amount: "", remarks: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) setForm({ amount: "", remarks: "" });
  }, [show]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!(Number(form.amount) > 0)) {
      toast.error("Bonus amount is required.");
      return;
    }
    setSaving(true);
    try {
      const res = await erpFetch("/scheme", {
        method: "POST",
        body: {
          type: "bonus_add",
          enrollment_id: enrollment.enrollment_id ?? enrollment.id,
          amount: Number(form.amount),
          remarks: form.remarks,
        },
      });
      if (isErpApiSuccess(res)) {
        toast.success("Bonus added successfully.");
        onSaved(res.data);
        onHide();
      } else {
        toast.error(res?.message || "Bonus add failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Bonus add failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" contentClassName={cardClass}>
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <div>
          <Modal.Title className="fs-5 fw-semibold mb-0">Add Bonus</Modal.Title>
          <div className="text-muted small mt-1">
            Card #{enrollment?.card_no} — {enrollment?.customer_name}
          </div>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body className="p-4">
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Amount (₹) *</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.remarks}
                  onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                  className={buttonClass}
                  placeholder="e.g. Year 1 adjustment"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className={buttonClass} onClick={onHide}>Cancel</Button>
          <Button variant="primary" className={buttonClass} type="submit" disabled={saving}>
            {saving ? "Saving..." : "Add Bonus"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── Bonus Divide Modal (plan pool) ──────────────────────────────────────────

function BonusDivideModal({ show, onHide, selectedPlan, onSaved }) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [form, setForm] = useState({
    total_amount: "",
    min_months_paid: "12",
    year_no: "1",
    remarks: "",
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (show) {
      setForm({
        total_amount: "",
        min_months_paid: "12",
        year_no: "1",
        remarks: "",
      });
      setResult(null);
    }
  }, [show]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!(Number(form.total_amount) > 0)) {
      toast.error("Total bonus amount is required.");
      return;
    }
    setSaving(true);
    try {
      const res = await erpFetch("/scheme", {
        method: "POST",
        body: {
          type: "bonus_divide",
          plan_id: selectedPlan.id,
          total_amount: Number(form.total_amount),
          min_months_paid: Number(form.min_months_paid) || 0,
          year_no: Number(form.year_no) || 1,
          remarks: form.remarks,
        },
      });
      if (isErpApiSuccess(res)) {
        setResult(res.data);
        toast.success("Bonus divided successfully.");
        onSaved(res.data);
      } else {
        toast.error(res?.message || "Bonus divide failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Bonus divide failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" contentClassName={cardClass}>
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <div>
          <Modal.Title className="fs-5 fw-semibold mb-0">Divide Bonus Pool</Modal.Title>
          <div className="text-muted small mt-1">Plan: {selectedPlan?.name}</div>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body className="p-4">
          <div
            className="border rounded small py-2 px-3 mb-1"
            style={{ background: "#F1F5F9", color: "#334155" }}
          >
            Eligible = ACTIVE/MATURED, not withdrawn, paid_count ≥ min months.
          </div>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Total Amount (₹) *</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.total_amount}
                  onChange={(e) => setForm((f) => ({ ...f, total_amount: e.target.value }))}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Min Months Paid</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={form.min_months_paid}
                  onChange={(e) => setForm((f) => ({ ...f, min_months_paid: e.target.value }))}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Year No</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={form.year_no}
                  onChange={(e) => setForm((f) => ({ ...f, year_no: e.target.value }))}
                  className={buttonClass}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.remarks}
                  onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                  className={buttonClass}
                  placeholder="e.g. Year 1 interest share"
                />
              </Form.Group>
            </Col>
          </Row>
          {result && (
            <div className="mt-3 p-3 rounded-3 border bg-success-subtle text-success small">
              {formatIndianRupee(result.total_amount)} ÷ {result.eligible_count} eligible ={" "}
              <strong>{formatIndianRupee(result.per_user_amount)}</strong> per user
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className={buttonClass} onClick={onHide}>
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button variant="primary" className={buttonClass} type="submit" disabled={saving}>
              {saving ? "Dividing..." : "Divide Bonus"}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── Enroll Customer Modal ───────────────────────────────────────────────────

function EnrollModal({ show, onHide, plans = [], selectedPlan, onPlanChange, onSaved }) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [planId, setPlanId] = useState("");
  const [form, setForm] = useState({
    start_date: TODAY,
    due_day: "1",
    installment_amount: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const enrollPlan =
    plans.find((p) => String(p.id) === String(planId)) || selectedPlan || null;
  const isFlexible = enrollPlan?.installment_type === "FLEXIBLE";

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePlanSelect = (id) => {
    setPlanId(id);
    const plan = plans.find((p) => String(p.id) === String(id)) || null;
    setForm((f) => ({
      ...f,
      installment_amount: plan?.default_amount ?? "",
    }));
    if (plan && onPlanChange) onPlanChange(plan);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!enrollPlan?.id) {
      toast.error("Please select a scheme plan.");
      return;
    }
    if (!selectedCustomer) {
      toast.error("Please select a customer.");
      return;
    }
    if (!form.start_date) {
      toast.error("Start date is required.");
      return;
    }
    if (!form.due_day) {
      toast.error("Due day is required.");
      return;
    }
    setSaving(true);
    try {
      const branchId = getActiveBranchId() || 1;
      const payload = {
        type: "create",
        branch_id: branchId,
        customer_id: selectedCustomer.id,
        plan_id: enrollPlan.id,
        start_date: form.start_date,
        due_day: Number(form.due_day),
        notes: form.notes,
      };
      if (isFlexible && form.installment_amount !== "") {
        payload.installment_amount = Number(form.installment_amount);
      } else if (!isFlexible && form.installment_amount !== "") {
        payload.installment_amount = Number(form.installment_amount);
      }
      const res = await erpFetch("/scheme", { method: "POST", body: payload });
      if (isErpApiSuccess(res)) {
        toast.success("Customer enrolled successfully.");
        if (onPlanChange) onPlanChange(enrollPlan);
        onSaved(res.data);
        onHide();
      } else {
        toast.error(res?.message || "Enrollment failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Enrollment failed."));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (show) {
      setSelectedCustomer(null);
      const initialId = selectedPlan?.id != null ? String(selectedPlan.id) : "";
      setPlanId(initialId);
      setForm({
        start_date: TODAY,
        due_day: "1",
        installment_amount: selectedPlan?.default_amount ?? "",
        notes: "",
      });
    }
  }, [show, selectedPlan]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      contentClassName={cardClass}
    >
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <Modal.Title className="fs-5 fw-semibold mb-0">Enroll Customer</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body className="p-4">
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Scheme Plan *</Form.Label>
                <Form.Select
                  value={planId}
                  onChange={(e) => handlePlanSelect(e.target.value)}
                  className={buttonClass}
                  required
                >
                  <option value="">— Select Scheme Plan —</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.code ? ` (${p.code})` : ""}
                      {p.default_amount != null
                        ? ` · ${formatIndianRupee(p.default_amount)}`
                        : ""}
                      {Number(p.is_mandal) === 1 ? " · Mandal" : ""}
                    </option>
                  ))}
                </Form.Select>
                {enrollPlan && (
                  <div className="text-muted small mt-1">
                    {enrollPlan.duration_months} months
                    {enrollPlan.installment_type
                      ? ` · ${enrollPlan.installment_type}`
                      : ""}
                    {enrollPlan.late_fee_amount != null
                      ? ` · Late fee ${formatIndianRupee(enrollPlan.late_fee_amount)}`
                      : ""}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Customer *</Form.Label>
                <CustomerPicker
                  value={selectedCustomer}
                  onChange={setSelectedCustomer}
                  buttonClass={buttonClass}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Start Date *</Form.Label>
                <Form.Control
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setF("start_date", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Due Day (1-31) *</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={31}
                  value={form.due_day}
                  onChange={(e) => setF("due_day", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            {isFlexible && (
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Installment Amount (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.installment_amount}
                    onChange={(e) => setF("installment_amount", e.target.value)}
                    className={buttonClass}
                    placeholder="Enter chosen monthly amount"
                  />
                </Form.Group>
              </Col>
            )}
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Notes / Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setF("notes", e.target.value)}
                  className={buttonClass}
                  placeholder="Optional enrollment notes..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className={buttonClass} onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className={buttonClass}
            type="submit"
            disabled={saving || !enrollPlan}
          >
            {saving ? "Enrolling..." : "Enroll Customer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── Edit Scheme Card Modal ──────────────────────────────────────────────────

function EditCardModal({ show, onHide, enrollment, selectedPlan, onSaved }) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const isFlexible =
    enrollment?.installment_type === "FLEXIBLE" ||
    selectedPlan?.installment_type === "FLEXIBLE";

  const [form, setForm] = useState({
    start_date: TODAY,
    due_day: "1",
    installment_amount: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && enrollment) {
      setForm({
        start_date: enrollment.start_date?.split("T")[0] || TODAY,
        due_day: enrollment.due_day != null ? String(enrollment.due_day) : "1",
        installment_amount: enrollment.installment_amount ?? "",
        notes: enrollment.notes ?? "",
      });
    }
  }, [show, enrollment]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.start_date) {
      toast.error("Start date is required.");
      return;
    }
    if (!form.due_day) {
      toast.error("Due day is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: "update",
        id: enrollment.id,
        enrollment_id: enrollment.id,
        start_date: form.start_date,
        due_day: Number(form.due_day),
        notes: form.notes,
      };
      if (isFlexible && form.installment_amount !== "") {
        payload.installment_amount = Number(form.installment_amount);
      }
      const res = await erpFetch("/scheme", { method: "POST", body: payload });
      if (isErpApiSuccess(res)) {
        toast.success("Scheme card updated successfully.");
        onSaved(res.data);
        onHide();
      } else {
        toast.error(res?.message || "Update failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Update failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      contentClassName={cardClass}
    >
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <div>
          <Modal.Title className="fs-5 fw-semibold mb-0">
            Edit Scheme Card — #{enrollment?.card_no}
          </Modal.Title>
          <div className="text-muted small mt-1">
            Customer: {enrollment?.customer_name} {enrollment?.customer_mobile && `(${enrollment.customer_mobile})`}
          </div>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body className="p-4">
          <Row className="g-3">
            <Col md={6}>
              <div className="p-3 rounded-2 bg-light border">
                <span className="text-muted small d-block">Plan Name</span>
                <span className="fw-semibold text-dark">
                  {enrollment?.plan_name || selectedPlan?.name || "—"}
                </span>
              </div>
            </Col>
            <Col md={6}>
              <div className="p-3 rounded-2 bg-light border">
                <span className="text-muted small d-block">Status</span>
                <StatusBadge status={enrollment?.status} />
              </div>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Start Date *</Form.Label>
                <Form.Control
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setF("start_date", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Due Day (1-31) *</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={31}
                  value={form.due_day}
                  onChange={(e) => setF("due_day", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            {isFlexible && (
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Installment Amount (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.installment_amount}
                    onChange={(e) => setF("installment_amount", e.target.value)}
                    className={buttonClass}
                  />
                </Form.Group>
              </Col>
            )}
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Notes / Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setF("notes", e.target.value)}
                  className={buttonClass}
                  placeholder="Card notes..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className={buttonClass} onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" className={buttonClass} type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── Card Detail View ────────────────────────────────────────────────────────

function CardDetail({ enrollmentId, plan, bankAccounts, onBack, onRefresh }) {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } = useButtonTableStyle();
  const canEdit = hasPermission(PERMISSIONS.SCHEME_EDIT);
  const canAdd = hasPermission(PERMISSIONS.SCHEME_ADD);
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);
  const [showReceive, setShowReceive] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showLoanCreate, setShowLoanCreate] = useState(false);
  const [showBonusAdd, setShowBonusAdd] = useState(false);
  const [repayLoan, setRepayLoan] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const valueModeIsGoldWeight = plan?.value_mode === "GOLD_WEIGHT";

  const fetchCard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await erpFetch("/scheme", {
        method: "POST",
        body: { type: "show", id: enrollmentId },
      });
      if (isErpApiSuccess(res)) {
        setCard(res.data);
      } else {
        toast.error(res?.message || "Failed to load card details.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Failed to load card details."));
    } finally {
      setLoading(false);
    }
  }, [enrollmentId]);

  const applyCardData = useCallback(
    (data) => {
      if (!data) {
        fetchCard();
        if (onRefresh) onRefresh();
        return;
      }
      if (data.enrollment || Array.isArray(data.entries)) {
        setCard(data);
      } else if (data.card) {
        setCard(data.card);
      } else {
        setCard(data);
        fetchCard();
      }
      if (onRefresh) onRefresh();
    },
    [fetchCard, onRefresh],
  );

  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  const handleEntryUpdate = async (payload) => {
    try {
      const res = await erpFetch("/scheme", { method: "POST", body: payload });
      if (isErpApiSuccess(res)) {
        toast.success("Entry updated successfully.");
        setCard(res.data);
      } else {
        toast.error(res?.message || "Update failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Update failed."));
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (
      !window.confirm(
        `Delete this ${entry.entry_type} entry for installment #${entry.installment_no}?`
      )
    )
      return;
    setDeletingId(entry.id);
    try {
      const res = await erpFetch("/scheme", {
        method: "POST",
        body: { type: "delete_entry", entry_id: entry.id },
      });
      if (isErpApiSuccess(res)) {
        toast.success("Entry deleted successfully.");
        setCard(res.data);
      } else {
        toast.error(res?.message || "Delete failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Delete failed."));
    } finally {
      setDeletingId(null);
    }
  };

  const enrollment = card?.enrollment ?? card;
  const entries = card?.entries ?? [];
  const loans = Array.isArray(card?.loans) ? card.loans : [];
  const planLoanPot = card?.plan_loan_pot;
  const isActiveLike = ["ACTIVE", "MATURED"].includes(enrollment?.status);
  const notWithdrawn = !Number(enrollment?.withdrawn);

  return (
    <div className={`card w-100 position-relative overflow-hidden ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""}`}>
      {/* Top Header */}
      <div className="px-4 py-3 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className={`btn btn-outline-secondary btn-sm ${buttonClass}`}
            onClick={onBack}
          >
            <Icon icon="solar:arrow-left-line-duotone" className="me-1" />
            Back to Cards
          </button>
          <h4 className="card-title mb-0">
            Card #{enrollment?.card_no || "—"} ({enrollment?.customer_name || "—"})
            {Number(enrollment?.is_mandal ?? plan?.is_mandal) === 1 && (
              <span className="badge bg-warning-subtle text-warning ms-2">Mandal</span>
            )}
          </h4>
        </div>

        {/* Action Buttons */}
        <div className="d-flex gap-2 flex-wrap">
          {canEdit && (
            <button
              type="button"
              className={`btn btn-outline-secondary btn-sm ${buttonClass}`}
              onClick={() => setShowEdit(true)}
            >
              <Icon icon="solar:pen-new-square-line-duotone" className="me-1" />
              Edit Card
            </button>
          )}
          {canAdd && isActiveLike && (
            <button
              type="button"
              className={`btn btn-success btn-sm ${buttonClass}`}
              onClick={() => setShowReceive(true)}
            >
              <Icon icon="solar:card-recive-line-duotone" className="me-1" />
              Receive Payment
            </button>
          )}
          {canEdit && isActiveLike && notWithdrawn && (
            <button
              type="button"
              className={`btn btn-primary btn-sm ${buttonClass}`}
              onClick={() => setShowWithdraw(true)}
            >
              <Icon icon="solar:wallet-money-line-duotone" className="me-1" />
              Withdraw
            </button>
          )}
          {canAdd && isActiveLike && notWithdrawn && (
            <button
              type="button"
              className={`btn btn-warning btn-sm ${buttonClass}`}
              onClick={() => setShowLoanCreate(true)}
            >
              <Icon icon="solar:hand-money-line-duotone" className="me-1" />
              Loan
            </button>
          )}
          {canEdit && isActiveLike && notWithdrawn && (
            <button
              type="button"
              className={`btn btn-info btn-sm ${buttonClass}`}
              onClick={() => setShowBonusAdd(true)}
            >
              <Icon icon="solar:gift-line-duotone" className="me-1" />
              Bonus
            </button>
          )}
          {canEdit && isActiveLike && (
            <button
              type="button"
              className={`btn btn-danger btn-sm ${buttonClass}`}
              onClick={() => setShowClose(true)}
            >
              <Icon icon="solar:close-circle-line-duotone" className="me-1" />
              Close Card
            </button>
          )}
        </div>
      </div>

      <div className="card-body p-4">
        {loading ? (
          <div className="text-center py-5 text-muted">Loading card details...</div>
        ) : (
          <>
            {/* Summary Stat Grid */}
            <div className="row g-3 mb-4">
              {[
                ["Doc Number", enrollment?.doc_number || "—", "solar:document-text-line-duotone", "#64748B"],
                ["Customer", `${enrollment?.customer_name || "—"} (${enrollment?.customer_mobile || "—"})`, "solar:user-circle-line-duotone", "#0d6efd"],
                ["Plan", enrollment?.plan_name || "—", "solar:layers-line-duotone", "#6f42c1"],
                ["Status", <StatusBadge key="status" status={enrollment?.status} />, "solar:check-circle-line-duotone", "#198754"],
                ["Hafta Paid", formatIndianRupee(enrollment?.paid_amount ?? 0), "solar:wallet-money-line-duotone", "#059669"],
                ["Bonus", formatIndianRupee(enrollment?.bonus_amount ?? 0), "solar:gift-line-duotone", "#d97706"],
                ["Late Fee", formatIndianRupee(enrollment?.late_fee_total ?? 0), "solar:danger-triangle-line-duotone", "#dc3545"],
                ["Skips", String(enrollment?.skip_count ?? 0), "solar:calendar-mark-line-duotone", "#d97706"],
                ["Open Loan", formatIndianRupee(numAmt(enrollment?.open_loan_principal) + numAmt(enrollment?.open_loan_interest)), "solar:card-send-line-duotone", "#b45309"],
                ["Redeemed", formatIndianRupee(enrollment?.redeemed_amount ?? 0), "solar:cart-check-line-duotone", "#0891b2"],
                ["Available", formatIndianRupee(enrollment?.available_amount ?? 0), "solar:hand-money-line-duotone", "#2563eb"],
                ["Withdrawable", formatIndianRupee(enrollment?.withdrawable_amount ?? 0), "solar:cash-out-line-duotone", "#0d6efd"],
              ].map(([label, val, icon, color]) => (
                <div key={label} className="col-12 col-sm-6 col-md-3">
                  <div
                    className="p-3 rounded-3 border d-flex align-items-center gap-3 h-100"
                    style={{ background: "#F8FAFC" }}
                  >
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: 38,
                        height: 38,
                        background: "#FFFFFF",
                        color: color,
                        border: "1px solid #E2E8F0",
                        flexShrink: 0,
                      }}
                    >
                      <Icon icon={icon} width={20} />
                    </div>
                    <div className="overflow-hidden">
                      <div className="text-muted small">{label}</div>
                      <div className="fw-bold text-dark text-truncate">{val}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {planLoanPot && (
              <div className="p-3 rounded-3 border mb-4 small" style={{ background: "#FFFBEB" }}>
                <div className="fw-semibold mb-2">Plan Loan Pot (this plan only)</div>
                <div className="row g-2">
                  <div className="col-md-4">
                    Gathered: <strong>{formatIndianRupee(planLoanPot.total_gathered)}</strong>
                  </div>
                  <div className="col-md-4">
                    Outstanding: <strong>{formatIndianRupee(planLoanPot.outstanding_loans)}</strong>
                  </div>
                  <div className="col-md-4">
                    Available: <strong className="text-primary">{formatIndianRupee(planLoanPot.available_for_loan)}</strong>
                  </div>
                </div>
              </div>
            )}

            {/* Installment Entries Table */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0 fw-semibold text-dark fs-4">Installment Timeline & Entries</h5>
              <span className="badge bg-light text-muted border">
                {entries.length} Entries Recorded
              </span>
            </div>

            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table className={`table text-nowrap mb-0 align-middle ${tableClass}`}>
                <thead className="text-dark fs-4">
                  <tr>
                    <th>#</th>
                    <th>Type</th>
                    <th>Month</th>
                    <th>Due Date</th>
                    <th>Receipt Date</th>
                    <th>Amount</th>
                    <th>Payment Mode</th>
                    <th>Gold Rate</th>
                    <th>Gold Grams</th>
                    <th>Source</th>
                    <th>Remarks</th>
                    {canEdit && <th className="text-end">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr>
                      <td colSpan={canEdit ? 12 : 11} className="text-center py-5 text-muted">
                        No installment entries found for this scheme card.
                      </td>
                    </tr>
                  ) : (
                    entries.map((entry) => (
                      <tr key={entry.id}>
                        <td>
                          <span className="fw-semibold">{entry.installment_no}</span>
                        </td>
                        <td>
                          <EntryTypeBadge type={entry.entry_type} />
                        </td>
                        <td>{entry.for_month || "—"}</td>
                        <td>{entry.due_date?.split("T")[0] || "—"}</td>
                        <td>{entry.receipt_date?.split("T")[0] || "—"}</td>
                        <td className="fw-bold text-dark">
                          {entry.amount != null ? formatIndianRupee(entry.amount) : "—"}
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {entry.payment_mode || "—"}
                          </span>
                        </td>
                        <td>{entry.gold_rate ? `₹${entry.gold_rate}` : "—"}</td>
                        <td>{entry.gold_grams ? `${entry.gold_grams} gm` : "—"}</td>
                        <td>{entry.source || "—"}</td>
                        <td style={{ maxWidth: 180 }} className="text-truncate">
                          {entry.remarks || "—"}
                        </td>
                        {canEdit && (
                          <td className="text-end">
                            <div className="d-flex gap-1 justify-content-end align-items-center">
                              {entry.entry_type === "PAYMENT" && (
                                <button
                                  type="button"
                                  className={`btn btn-primary btn-sm ${buttonClass}`}
                                  onClick={() => setEditEntry(entry)}
                                  title="Edit Entry"
                                >
                                  <Icon icon="solar:pen-bold" width="16" height="16" />
                                </button>
                              )}
                              {(entry.entry_type === "PAYMENT" || entry.entry_type === "SKIP") && (
                                <button
                                  type="button"
                                  className={`btn btn-danger btn-sm ${buttonClass}`}
                                  disabled={deletingId === entry.id}
                                  onClick={() => handleDeleteEntry(entry)}
                                  title={
                                    entry.entry_type === "SKIP"
                                      ? "Delete SKIP to allow receipt"
                                      : "Delete Entry"
                                  }
                                >
                                  <Icon icon="solar:trash-bin-trash-bold" width="16" height="16" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Loans */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0 fw-semibold text-dark fs-4">Loans</h5>
              <span className="badge bg-light text-muted border">{loans.length} Loans</span>
            </div>
            <div className={`table-responsive mb-2 border ${cardClass}`}>
              <table className={`table text-nowrap mb-0 align-middle ${tableClass}`}>
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Loan #</th>
                    <th>Date</th>
                    <th>Principal</th>
                    <th>Interest</th>
                    <th>EMI</th>
                    <th>Status</th>
                    {canAdd && <th className="text-end">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {loans.length === 0 ? (
                    <tr>
                      <td colSpan={canAdd ? 7 : 6} className="text-center py-4 text-muted">
                        No loans on this card.
                      </td>
                    </tr>
                  ) : (
                    loans.map((loan) => (
                      <tr key={loan.id}>
                        <td className="fw-semibold">#{loan.id}</td>
                        <td>{loan.loan_date?.split?.("T")?.[0] || "—"}</td>
                        <td>{formatIndianRupee(loan.principal ?? loan.principal_amount ?? 0)}</td>
                        <td>{formatIndianRupee(loan.interest_amount ?? 0)}</td>
                        <td>{formatIndianRupee(loan.emi_amount ?? 0)}</td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {loan.status || "—"}
                          </span>
                        </td>
                        {canAdd && (
                          <td className="text-end">
                            {String(loan.status || "").toUpperCase() === "OPEN" && (
                              <button
                                type="button"
                                className={`btn btn-success btn-sm ${buttonClass}`}
                                onClick={() => setRepayLoan(loan)}
                              >
                                Repay
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <ReceiveModal
        show={showReceive}
        onHide={() => setShowReceive(false)}
        enrollment={enrollment}
        selectedPlan={plan}
        bankAccounts={bankAccounts}
        onSaved={applyCardData}
      />
      {showEdit && (
        <EditCardModal
          show={showEdit}
          onHide={() => setShowEdit(false)}
          enrollment={enrollment}
          selectedPlan={plan}
          onSaved={applyCardData}
        />
      )}
      <WithdrawModal
        show={showWithdraw}
        onHide={() => setShowWithdraw(false)}
        enrollment={enrollment}
        bankAccounts={bankAccounts}
        onSaved={applyCardData}
      />
      <LoanCreateModal
        show={showLoanCreate}
        onHide={() => setShowLoanCreate(false)}
        enrollment={enrollment}
        bankAccounts={bankAccounts}
        onSaved={applyCardData}
      />
      <BonusAddModal
        show={showBonusAdd}
        onHide={() => setShowBonusAdd(false)}
        enrollment={enrollment}
        onSaved={applyCardData}
      />
      {repayLoan && (
        <LoanRepayModal
          show={!!repayLoan}
          onHide={() => setRepayLoan(null)}
          enrollment={enrollment}
          loan={repayLoan}
          bankAccounts={bankAccounts}
          onSaved={(data) => {
            applyCardData(data);
            setRepayLoan(null);
          }}
        />
      )}
      <CloseModal
        show={showClose}
        onHide={() => setShowClose(false)}
        enrollment={enrollment}
        bankAccounts={bankAccounts}
        onSaved={(data) => {
          if (data) setCard(data);
          onBack();
        }}
      />
      {editEntry && (
        <EntryEditModal
          show={!!editEntry}
          onHide={() => setEditEntry(null)}
          entry={editEntry}
          bankAccounts={bankAccounts}
          valueModeIsGoldWeight={valueModeIsGoldWeight}
          onSave={handleEntryUpdate}
        />
      )}
    </div>
  );
}

// ─── Main SchemeCardList Component ───────────────────────────────────────────

function SchemeCardList() {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } = useButtonTableStyle();
  const canView = hasPermission(PERMISSIONS.SCHEME_VIEW);
  const canAdd = hasPermission(PERMISSIONS.SCHEME_ADD);
  const canEdit = hasPermission(PERMISSIONS.SCHEME_EDIT);

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [showBonusDivide, setShowBonusDivide] = useState(false);
  const [editModalEnrollment, setEditModalEnrollment] = useState(null);
  const [receiveModalEnrollment, setReceiveModalEnrollment] = useState(null);
  const [closeModalEnrollment, setCloseModalEnrollment] = useState(null);
  const [viewEnrollmentId, setViewEnrollmentId] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Load plans for dropdown
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const branchId = getActiveBranchId() || 1;
        const res = await erpFetch("/scheme_plan", {
          method: "POST",
          body: { type: "list", status: 1, limit: 100, branch_id: branchId },
        });
        if (isErpApiSuccess(res)) {
          const items = res.data?.items ?? res.data ?? [];
          setPlans(items);
          // Keep current selection; null = All Schemes
          setSelectedPlan((prev) => {
            if (prev == null) return null;
            return items.find((p) => String(p.id) === String(prev.id)) || null;
          });
        }
      } catch {
        // ignore
      }
    };
    fetchPlans();
  }, []);

  // Load bank accounts
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const branchId = getActiveBranchId() || 1;
        const res = await listBankAccounts({ branch_id: branchId });
        const items = res?.data?.items ?? res?.data ?? [];
        setBankAccounts(Array.isArray(items) ? items : []);
      } catch {
        setBankAccounts([]);
      }
    };
    fetchBanks();
  }, []);

  const pageCache = useVisitedPageCache();

  const fetchList = useCallback(async (force = false) => {
    if (!canView) return;
    const branchId = getActiveBranchId() || 1;
    const planKey = selectedPlan?.id ?? "all";
    const cacheKey = visitedPageKey([
      branchId,
      planKey,
      debouncedSearch,
      statusFilter,
      pagination.page,
      pagination.limit,
    ]);
    if (force) pageCache.clear();
    const cached = force ? null : pageCache.read(cacheKey);
    if (cached) {
      setList(cached.items);
      setPagination((p) => ({ ...p, total: cached.total }));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const body = {
        type: "list",
        search: debouncedSearch,
        status: statusFilter,
        page: pagination.page,
        limit: pagination.limit,
        branch_id: branchId,
      };
      if (selectedPlan?.id) body.plan_id = selectedPlan.id;

      const res = await erpFetch("/scheme", {
        method: "POST",
        body,
      });
      if (isErpApiSuccess(res)) {
        const items = res.data?.items ?? res.data ?? [];
        const total = res.data?.pagination?.total ?? items.length ?? 0;
        setList(items);
        setPagination((p) => ({ ...p, total }));
        pageCache.write(cacheKey, { items, total });
      } else if (!selectedPlan && plans.length > 0) {
        // Fallback: API may require plan_id — merge lists from every plan
        const pages = await Promise.all(
          plans.map((plan) =>
            erpFetch("/scheme", {
              method: "POST",
              body: {
                type: "list",
                plan_id: plan.id,
                search: debouncedSearch,
                status: statusFilter,
                page: 1,
                limit: 500,
                branch_id: branchId,
              },
            }).catch(() => null),
          ),
        );
        const merged = [];
        pages.forEach((r) => {
          if (!r || !isErpApiSuccess(r)) return;
          const items = r.data?.items ?? r.data ?? [];
          if (Array.isArray(items)) merged.push(...items);
        });
        const start = (pagination.page - 1) * pagination.limit;
        const pageItems = merged.slice(start, start + pagination.limit);
        setList(pageItems);
        setPagination((p) => ({ ...p, total: merged.length }));
        pageCache.write(cacheKey, { items: pageItems, total: merged.length });
      } else {
        toast.error(res?.message || "Failed to load scheme cards.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Failed to load scheme cards."));
    } finally {
      setLoading(false);
    }
  }, [
    canView,
    selectedPlan,
    plans,
    debouncedSearch,
    statusFilter,
    pagination.page,
    pagination.limit,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        {viewEnrollmentId ? (
          <CardDetail
            enrollmentId={viewEnrollmentId}
            plan={
              selectedPlan ||
              plans.find(
                (p) =>
                  String(p.id) ===
                  String(
                    list.find((r) => String(r.id) === String(viewEnrollmentId))
                      ?.plan_id,
                  ),
              ) ||
              null
            }
            bankAccounts={bankAccounts}
            onBack={() => setViewEnrollmentId(null)}
            onRefresh={fetchList}
          />
        ) : (
          <div className={`card w-100 position-relative overflow-hidden ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""}`}>
            {/* Header */}
            <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
              <div>
                <h4 className="card-title mb-0">Scheme Card List</h4>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {canEdit && selectedPlan?.id && (
                  <button
                    type="button"
                    className={`btn btn-outline-primary ${buttonClass}`}
                    onClick={() => setShowBonusDivide(true)}
                  >
                    <Icon icon="solar:gift-line-duotone" className="me-1" width={18} />
                    Divide Bonus
                  </button>
                )}
                {canAdd && plans.length > 0 && (
                  <button
                    id="scheme-card-add-btn"
                    type="button"
                    className={`btn btn-success ${buttonClass}`}
                    onClick={() => setShowEnroll(true)}
                  >
                    <Icon icon="solar:user-plus-rounded-line-duotone" className="me-1" width={18} />
                    Add Customer
                  </button>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="card-body p-4">
              {/* Search & Filter Bar */}
              <div className="d-flex gap-3 mb-3 align-items-center flex-wrap">
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  style={{ maxWidth: "260px" }}
                  placeholder="Search customer, mobile, card..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                />

                <div className="d-flex gap-2 ms-auto flex-wrap align-items-center">
                  <div style={{ minWidth: 220 }}>
                    <select
                      className={`form-select ${buttonClass}`}
                      value={selectedPlan?.id ?? ""}
                      onChange={(e) => {
                        const p = plans.find((x) => String(x.id) === e.target.value);
                        setSelectedPlan(p ?? null);
                        setPagination((pv) => ({ ...pv, page: 1 }));
                      }}
                    >
                      <option value="">All Schemes</option>
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ minWidth: 150 }}>
                    <select
                      className={`form-select ${buttonClass}`}
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPagination((p) => ({ ...p, page: 1 }));
                      }}
                    >
                      <option value="">All Statuses</option>
                      {STATUS_OPTIONS.filter(Boolean).map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className={`table-responsive mb-4 border ${cardClass}`}>
                <table className={`table text-nowrap mb-0 align-middle ${tableClass}`}>
                  <thead className="text-dark fs-4">
                    <tr>
                      <th>Card No</th>
                      <th>Customer Name</th>
                      <th>Mobile</th>
                      <th>Start Date</th>
                      <th>Installment</th>
                      <th>Paid</th>
                      <th>Skips</th>
                      <th>Hafta</th>
                      <th>Bonus</th>
                      <th>Late Fee</th>
                      <th>Withdrawable</th>
                      <th>Status</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={13} className="text-center py-5 text-muted">
                          Loading scheme cards...
                        </td>
                      </tr>
                    ) : list.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="text-center py-5 text-muted">
                          {selectedPlan
                            ? "No scheme cards found for this plan."
                            : "No scheme cards found."}
                        </td>
                      </tr>
                    ) : (
                      list.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <span className="fw-bold text-dark">#{row.card_no}</span>
                            {Number(row.withdrawn) === 1 && (
                              <span className="badge bg-secondary-subtle text-secondary ms-1">W</span>
                            )}
                          </td>
                          <td>
                            <span className="fw-semibold text-dark">{row.customer_name}</span>
                          </td>
                          <td>{row.customer_mobile || "—"}</td>
                          <td>{row.start_date?.split("T")[0] || "—"}</td>
                          <td>
                            {row.installment_amount != null
                              ? formatIndianRupee(row.installment_amount)
                              : "—"}
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border">
                              {row.paid_count ?? 0}
                            </span>
                          </td>
                          <td>{row.skip_count ?? 0}</td>
                          <td className="fw-semibold text-dark">
                            {formatIndianRupee(row.paid_amount ?? 0)}
                          </td>
                          <td className="text-success fw-medium">
                            {formatIndianRupee(row.bonus_amount ?? 0)}
                          </td>
                          <td className="text-danger">
                            {formatIndianRupee(row.late_fee_total ?? 0)}
                          </td>
                          <td className="fw-bold text-primary">
                            {formatIndianRupee(row.withdrawable_amount ?? row.available_amount ?? 0)}
                          </td>
                          <td>
                            <StatusBadge status={row.status} />
                          </td>
                          <td className="text-end">
                            <div className="d-flex gap-1 justify-content-end align-items-center">
                              <button
                                type="button"
                                className={`btn btn-info btn-sm ${buttonClass}`}
                                onClick={() => setViewEnrollmentId(row.id)}
                                title="View Card Details"
                                aria-label="View Card Details"
                              >
                                <Icon icon="solar:eye-bold" width="16" height="16" />
                              </button>
                              {canEdit && (
                                <button
                                  type="button"
                                  className={`btn btn-primary btn-sm ${buttonClass}`}
                                  onClick={() => setEditModalEnrollment(row)}
                                  title="Edit Card"
                                  aria-label="Edit Card"
                                >
                                  <Icon icon="solar:pen-bold" width="16" height="16" />
                                </button>
                              )}
                              {canAdd && ["ACTIVE", "MATURED"].includes(row.status) && (
                                <button
                                  type="button"
                                  className={`btn btn-success btn-sm ${buttonClass}`}
                                  onClick={() => setReceiveModalEnrollment(row)}
                                  title="Receive Payment"
                                  aria-label="Receive Payment"
                                >
                                  <Icon icon="solar:card-recive-bold" width="16" height="16" />
                                </button>
                              )}
                              {canEdit && ["ACTIVE", "MATURED"].includes(row.status) && (
                                <button
                                  type="button"
                                  className={`btn btn-danger btn-sm ${buttonClass}`}
                                  onClick={() => setCloseModalEnrollment(row)}
                                  title="Close Card"
                                  aria-label="Close Card"
                                >
                                  <Icon icon="solar:close-circle-bold" width="16" height="16" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total > 0 && (
                <Pagination
                  totalItems={pagination.total}
                  itemsPerPage={pagination.limit}
                  currentPage={pagination.page}
                  onPageChange={(p) => setPagination((pv) => ({ ...pv, page: p }))}
                  onItemsPerPageChange={(l) =>
                    setPagination((pv) => ({ ...pv, limit: l, page: 1 }))
                  }
                  itemsPerPageOptions={[10, 20, 30, 50, 100]}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <EnrollModal
        show={showEnroll}
        onHide={() => setShowEnroll(false)}
        plans={plans}
        selectedPlan={selectedPlan}
        onPlanChange={setSelectedPlan}
        onSaved={() => fetchList(true)}
      />
      <BonusDivideModal
        show={showBonusDivide}
        onHide={() => setShowBonusDivide(false)}
        selectedPlan={selectedPlan}
        onSaved={() => fetchList(true)}
      />
      {editModalEnrollment && (
        <EditCardModal
          show={!!editModalEnrollment}
          onHide={() => setEditModalEnrollment(null)}
          enrollment={editModalEnrollment}
          selectedPlan={selectedPlan}
          onSaved={() => fetchList(true)}
        />
      )}
      {receiveModalEnrollment && (
        <ReceiveModal
          show={!!receiveModalEnrollment}
          onHide={() => setReceiveModalEnrollment(null)}
          enrollment={receiveModalEnrollment}
          selectedPlan={selectedPlan}
          bankAccounts={bankAccounts}
          onSaved={() => fetchList(true)}
        />
      )}
      {closeModalEnrollment && (
        <CloseModal
          show={!!closeModalEnrollment}
          onHide={() => setCloseModalEnrollment(null)}
          enrollment={closeModalEnrollment}
          bankAccounts={bankAccounts}
          onSaved={() => fetchList(true)}
        />
      )}
    </div>
  );
}

export default SchemeCardList;
